import {useEffect, useState} from "react";
import {useSelector} from "react-redux";
import {useNavigate} from "react-router-dom";
import moment from "moment";

import Loading from "@/components/Loading";
import Page from "@/components/Page";
import MainActions from "@/components/MainActions";
import Breadcrumbs from "@/components/Breadcrumbs";
import VLFCard from "@/components/Card";
import VLFDatePicker from "@/components/Input/DatePicker";
import DataTable from "@/components/DataTable";
import HandlePlayersDataModal from "@/pages/AnthropometricData/handlePlayersData";

import {PlayerAPI} from "@/api/player";
import {RosterAPI} from "@/api/roster";

import AddIcon from "@mui/icons-material/Add";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import CloseIcon from "@mui/icons-material/Close";
import SaveIcon from "@mui/icons-material/Save";

import {
	ERROR_OPERATION,
	getInternalClubId,
	SUCCESSFUL_OPERATION,
} from "@/utils/constants";
import {useCategories, usePlayers} from "@/utils/hooks";
import {visibleRosters} from "@/utils/functions";

const BACK_PATH = "/measurations/wellness";

// Recuperi (TQR) inseriti dallo staff per più giocatori nello stesso giorno
function HandleTQRPage(props) {
	const {handleNotification} = props;

	const navigate = useNavigate();
	const seasonId = useSelector((state) => state?.season?.id);

	const [internalTeams, setInternalTeams] = useState([]);
	const getTeams = async () => {
		try {
			const response = await RosterAPI.list({
				seasonId,
				prospect: false,
				clubId: getInternalClubId(),
			});
			setInternalTeams(
				visibleRosters(
					(response || []).filter(
						(r) => !r.clubId || r.clubId === getInternalClubId(),
					),
				),
			);
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		}
	};
	useEffect(() => {
		if (seasonId) {
			getTeams();
		}
	}, [seasonId]);

	const {categories} = useCategories({
		setLoading: false,
		handleNotification,
		formatted: true,
		onlyPro: true,
	});
	const {players} = usePlayers({
		setLoading: false,
		handleNotification,
		goalkeepers: false,
		onlyInternal: true,
		prospect: false,
	});

	const [loading, setLoading] = useState(false);
	const [fields, setFields] = useState({});

	const [openModal, setOpenModal] = useState(false);
	const [initialData, setInitialData] = useState();
	const [playersList, setPlayersList] = useState([]);

	const saveTQR = async () => {
		setLoading(true);
		try {
			await PlayerAPI.createRecoveries(
				playersList.map((p) => ({
					playerId: p.player.value,
					tqrValue: p.tqr.value,
					tqrDate: moment(fields.tqrDate).format("YYYY-MM-DD"),
				})),
			);
			navigate(BACK_PATH);
			handleNotification(SUCCESSFUL_OPERATION, "success");
		} catch {
			handleNotification(ERROR_OPERATION, "error");
		} finally {
			setLoading(false);
		}
	};

	const removePlayer = (item) =>
		setPlayersList(
			playersList.filter((p) => p.player.value !== item.player.value),
		);

	const columns = [
		{
			key: "player",
			label: "Giocatore",
			render: (item) => item.player?.label,
			sortValue: (item) => item.player?.label,
		},
		{key: "tqr", label: "Recupero (TQR)", render: (item) => item.tqr?.label},
		{
			key: "remove",
			label: "",
			align: "end",
			render: (item) => (
				<div onClick={(e) => e.stopPropagation()}>
					<MainActions
						actions={[
							{
								icon: <CloseIcon />,
								title: "Togli",
								variant: "outlined",
								onClick: () => removePlayer(item),
							},
						]}
					/>
				</div>
			),
		},
	];

	const mainActions = [
		{
			icon: <ArrowBackIcon />,
			title: "Indietro",
			onClick: () => navigate(BACK_PATH),
			color: "info",
		},
		{
			icon: <SaveIcon />,
			title: "Salva",
			onClick: saveTQR,
			disabled: !fields.tqrDate || !playersList.length,
		},
	];

	const render = (
		<div className="d-flex flex-column gap-2">
			<VLFCard
				content={
					<div className="row no-margin">
						<div className="col-12 col-md-4 padding-small">
							<VLFDatePicker
								label="Data"
								value={fields.tqrDate}
								onChange={(value) => setFields({...fields, tqrDate: value})}
								required
								size="small"
							/>
						</div>
					</div>
				}
			/>
			<VLFCard
				title="Recuperi"
				content={
					<>
						<div className="main-actions-bar">
							<MainActions
								actions={[
									{
										icon: <AddIcon />,
										label: "Aggiungi giocatori",
										onClick: () => setOpenModal(true),
									},
								]}
							/>
						</div>
						<DataTable
							columns={columns}
							rows={playersList.map((p) => ({...p, id: p.player.value}))}
							onRowClick={(item) => {
								setInitialData(Object.assign({}, item));
								setOpenModal(true);
							}}
							empty="Aggiungi i giocatori, uno alla volta o tutta la rosa."
						/>
					</>
				}
			/>
			{openModal ? (
				<HandlePlayersDataModal
					type="tqr"
					open={openModal}
					confirm={(values) => {
						if (initialData) {
							const updatedList = Array.from(playersList);
							const index = updatedList.findIndex(
								(item) => item.player.value === values.player.value,
							);
							updatedList[index] = {
								...updatedList[index],
								...values,
							};
							setPlayersList(updatedList);
						} else {
							if (values.player) {
								setPlayersList([...playersList, values]);
							}
							if (values.players) {
								const multiplePlayers = [];
								Object.keys(values.players)?.forEach((pk) => {
									if (values.players[pk].tqr) {
										multiplePlayers.push(values.players[pk]);
									}
								});
								setPlayersList([...playersList, ...multiplePlayers]);
							}
						}
						setOpenModal(false);
						setInitialData(undefined);
					}}
					close={() => {
						setOpenModal(false);
						setInitialData(undefined);
					}}
					initialData={initialData}
					categories={categories}
					teams={internalTeams}
					players={players?.filter((p) => {
						const isInList = playersList?.filter(
							(pl) => pl.player.value === p.id,
						)?.[0];
						return !isInList;
					})}
				/>
			) : null}
		</div>
	);

	return (
		<Page>
			<Loading visible={loading} />
			<Breadcrumbs
				elements={[{label: "Benessere", path: BACK_PATH}, {label: "/"}]}
				current="Inserisci recuperi"
				actions={<MainActions actions={mainActions} />}
			/>
			<div className="row no-margin">
				<div className="col-sm-12 padding-small">{render}</div>
			</div>
		</Page>
	);
}

export default HandleTQRPage;
